/**
 * draftStaleNotice — is the stale-base warning actually about anything?
 *
 * REPORTED BY THE FLEET OWNER, 2026-09-17, right after their change merged and deployed:
 * "Even though latest change have been merged and deployed it still says I can discard draft
 * and must click the link to get the latest. that is maybe required but sure feels like an
 * added step."
 *
 * THE NOTICE WAS RIGHT ABOUT ITS FACT AND WRONG ABOUT ITS CONCLUSION. It fires on
 * `sess.base_ref !== fleetSha` — the fleet sha this draft was last checked against has
 * moved — which is true, and is true *because* the operator's own change shipped. The
 * sentence it draws from that fact is about content: "proposing it could revert someone
 * else's change", remedied by "Start fresh from the deployed version". When the draft is
 * byte-identical to the deployed file, all of that is false: proposing changes nothing,
 * and starting fresh hands back the same bytes.
 *
 * SO THE FACT IS KEPT AND THE CONCLUSION IS EARNED. A base-sha move is evidence that the
 * content MIGHT have diverged; where both texts are in hand it is no longer evidence at
 * all, because the answer is directly observable. This is the same correction the rest of
 * this programme has been making — the Results card, the run slot, the propose panel, the
 * CURRENT badge — one level up: those reported a stale reading of a fact, this reported a
 * conclusion its fact never supported.
 *
 * AND THE OTHER DIRECTION IS DELIBERATELY UNTOUCHED. Where the texts differ, the warning
 * stands exactly as it was. Adopting the deployed version silently would destroy an edit
 * somebody made, which is a far worse defect than an extra click; the owner was asked and
 * drew the line in the same place.
 */

/** What the caller knows at render time. */
export interface StaleNoticeInput {
  /** The base sha the restore effect found to have moved, or null if it has not. */
  staleBase: string | null
  /** The text in the editor right now. */
  draftYAML: string
  /**
   * The deployed copy of the file at this slot's own path, or null when there is none to
   * compare against — a scaffold that has never been proposed, or a detail read that has
   * not landed. Null means "cannot demonstrate", never "identical".
   */
  deployedYAML: string | null
  /**
   * How many OTHER files the changeset carries. A changeset is not its primary file:
   * `detail.yaml` is the deployed copy of this one path and of nothing else, so an
   * identical primary says nothing about a supporting file riding along — and a supporting
   * file can revert a change just as well.
   */
  extraFileCount: number
}

/**
 * staleNoticeApplies reports whether the stale-base warning has something to warn about.
 *
 * FAILS TOWARD SHOWING IT. Every case where identity cannot be DEMONSTRATED keeps the
 * notice: no deployed copy in hand, a changeset wider than the one file, or two texts that
 * differ by a single byte. Suppressing a real staleness warning would let a propose revert
 * somebody's work, so the only case that suppresses is the one where the proposal provably
 * changes nothing.
 */
export function staleNoticeApplies(inp: StaleNoticeInput): boolean {
  if (!inp.staleBase) return false
  if (inp.deployedYAML === null) return true
  if (inp.extraFileCount > 0) return true
  // BYTE-IDENTICAL, not normalised. A trailing newline difference is a real difference in
  // the file that would be proposed, and "close enough" is how a surface starts asserting
  // things it has not checked.
  return inp.draftYAML !== inp.deployedYAML
}
